import { FC, useContext } from 'react';
import {
  Paper,
  Group,
  Stack,
  Text,
  Badge,
  Collapse,
  ActionIcon,
  Box,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PedigreeCondition } from '../types';
import { getContrastingColor } from '../utils';
import { ConditionForm } from './ConditionForm';
import { PedigreeContext } from '../context/PedigreeContext';
import { useGraphData } from '../hooks/useGraphData';

// -----------------------------------------------------------------------------
export const PedigreeLegend: FC = () => {
  // ---------------------------------------------------------------------------
  const { t } = useTranslation('pedigree');
  const { mode } = useContext(PedigreeContext) || {};
  const { conditions, handleAddCondition, handleRemoveCondition } =
    useGraphData();

  const [isExpanded, { toggle: onToggleExpanded }] = useDisclosure(true);
  const [isGenderExpanded, { toggle: toggleGender }] = useDisclosure(false);
  const [isStatusExpanded, { toggle: toggleStatus }] = useDisclosure(false);

  // ---------------------------------------------------------------------------
  const renderConditionPreview = (condition: PedigreeCondition) => {
    const pattern = condition.pattern || 'solid';

    if (pattern === 'solid') {
      return (
        <Badge
          size="xs"
          variant="filled"
          style={{
            backgroundColor: condition.color,
            color: '#fff',
            minWidth: '18px', // 50% larger (was 12px)
            height: '18px', // 50% larger (was 12px)
            fontSize: '10px', // Slightly larger font
          }}
        >
          {condition.symbol}
        </Badge>
      );
    }

    // For patterns, use SVG - 50% larger
    const contrastColor = getContrastingColor(condition.color);

    return (
      <svg width="18" height="18" style={{ flexShrink: 0 }}>
        <defs>
          <pattern
            id={`pattern-${condition.id}`}
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
          >
            <rect width="6" height="6" fill={condition.color} />
            {pattern === 'horizontal' && (
              <>
                <line
                  x1="0"
                  y1="2"
                  x2="6"
                  y2="2"
                  stroke={contrastColor}
                  strokeWidth="1.2"
                />
                <line
                  x1="0"
                  y1="4"
                  x2="6"
                  y2="4"
                  stroke={contrastColor}
                  strokeWidth="1.2"
                />
              </>
            )}
            {pattern === 'vertical' && (
              <>
                <line
                  x1="2"
                  y1="0"
                  x2="2"
                  y2="6"
                  stroke={contrastColor}
                  strokeWidth="1.2"
                />
                <line
                  x1="4"
                  y1="0"
                  x2="4"
                  y2="6"
                  stroke={contrastColor}
                  strokeWidth="1.2"
                />
              </>
            )}
            {pattern === 'diagonal' && (
              <>
                <line
                  x1="0"
                  y1="0"
                  x2="6"
                  y2="6"
                  stroke={contrastColor}
                  strokeWidth="1.2"
                />
                <line
                  x1="0"
                  y1="3"
                  x2="3"
                  y2="6"
                  stroke={contrastColor}
                  strokeWidth="1.2"
                />
                <line
                  x1="3"
                  y1="0"
                  x2="6"
                  y2="3"
                  stroke={contrastColor}
                  strokeWidth="1.2"
                />
              </>
            )}
          </pattern>
        </defs>
        <rect
          width="18"
          height="18"
          fill={`url(#pattern-${condition.id})`}
          stroke="#333"
          strokeWidth="1"
        />
        {condition.symbol && (
          <text
            x="9"
            y="12"
            textAnchor="middle"
            fontSize="8"
            fill={contrastColor}
            fontWeight="bold"
          >
            {condition.symbol}
          </text>
        )}
      </svg>
    );
  };

  // ---------------------------------------------------------------------------
  return (
    <Paper
      shadow="md"
      withBorder
      style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 1000,
        minWidth: isExpanded ? '200px' : 'auto',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Legend Toggle Button */}
      <Group
        justify="space-between"
        p="sm"
        style={{
          cursor: 'pointer',
          borderBottom: isExpanded
            ? '1px solid var(--mantine-color-gray-3)'
            : 'none',
        }}
        onClick={onToggleExpanded}
        bg="gray.0"
      >
        <Group gap="xs">
          <Text size="sm" fw={600}>
            {t('legend.title')}
          </Text>
          <Text size="xs" c="dimmed">
            ({conditions.length} {t('legend.conditions')})
          </Text>
        </Group>
        <ActionIcon
          variant="subtle"
          size="sm"
          style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        >
          <IconChevronDown size={14} />
        </ActionIcon>
      </Group>

      {/* Expanded Content */}
      <Collapse in={isExpanded}>
        <Stack gap="md" p="sm">
          {' '}
          {/* Gender Symbols */}
          <Box>
            <Group
              justify="space-between"
              align="center"
              style={{ cursor: 'pointer' }}
              onClick={toggleGender}
            >
              <Text size="xs" fw={600}>
                {t('legend.genderIndicators')}
              </Text>
              <ActionIcon
                variant="subtle"
                size="xs"
                style={{
                  transform: isGenderExpanded
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                }}
              >
                <IconChevronDown size={12} />
              </ActionIcon>
            </Group>
            <Collapse in={isGenderExpanded}>
              <Stack gap={4} mt="xs">
                <Group gap="sm" align="center">
                  <Box
                    w={20}
                    h={20}
                    bg="blue.0"
                    style={{
                      border: '2px solid var(--mantine-color-blue-6)',
                      borderRadius: 0,
                    }}
                  />
                  <Text size="xs">{t('legend.male')}</Text>
                </Group>
                <Group gap="sm" align="center">
                  <Box
                    w={20}
                    h={20}
                    bg="pink.0"
                    style={{
                      border: '2px solid var(--mantine-color-pink-6)',
                      borderRadius: '50%',
                    }}
                  />
                  <Text size="xs">{t('legend.female')}</Text>
                </Group>
                <Group gap="sm" align="center">
                  <svg width="20" height="20" style={{ flexShrink: 0 }}>
                    <polygon
                      points="10,2 18,10 10,18 2,10"
                      fill="var(--mantine-color-gray-1)"
                      stroke="var(--mantine-color-gray-6)"
                      strokeWidth="2"
                    />
                  </svg>
                  <Text size="xs">{t('legend.unknown')}</Text>
                </Group>
              </Stack>
            </Collapse>
          </Box>
          {/* Status indicators */}
          <Box>
            <Group
              justify="space-between"
              align="center"
              style={{ cursor: 'pointer' }}
              onClick={toggleStatus}
            >
              {' '}
              <Text size="xs" fw={600}>
                {t('legend.statusIndicators')}
              </Text>
              <ActionIcon
                variant="subtle"
                size="xs"
                style={{
                  transform: isStatusExpanded
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                }}
              >
                <IconChevronDown size={12} />
              </ActionIcon>
            </Group>
            <Collapse in={isStatusExpanded}>
              <Stack gap={2} mt="xs">
                <Text size="xs" c="dimmed">
                  {t('legend.statusDescription.proband')}
                </Text>
                <Text size="xs" c="dimmed">
                  {t('legend.statusDescription.deceased')}
                </Text>
                <Text size="xs" c="dimmed">
                  {t('legend.statusDescription.adopted')}
                </Text>
                <Text size="xs" c="dimmed">
                  {t('legend.statusDescription.conditions')}
                </Text>
              </Stack>
            </Collapse>
          </Box>
          {/* Conditions */}
          {conditions.length > 0 && (
            <Box>
              <Text size="xs" fw={600} mb="xs">
                {t('legend.conditionsTitle')}
              </Text>
              <Stack gap={4}>
                {conditions.map((condition) => (
                  <Group key={condition.id} gap="xs" justify="space-between">
                    <Group gap="xs" style={{ flex: 1 }}>
                      {renderConditionPreview(condition)}
                      <Text size="xs" style={{ flex: 1 }}>
                        {condition.name}
                      </Text>
                    </Group>
                    {mode === 'edit' && (
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => handleRemoveCondition(condition.id)}
                      >
                        <IconX size={10} />
                      </ActionIcon>
                    )}
                  </Group>
                ))}
              </Stack>
            </Box>
          )}
          {/* Add Condition Form */}
          {mode === 'edit' && (
            <ConditionForm
              onAddCondition={handleAddCondition}
              existingConditions={conditions}
              buttonLabel={t('legend.addCondition')}
              buttonVariant="filled"
              buttonSize="xs"
              fullWidth
            />
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
};
